import React, { useState } from "react";
import { companyInfo } from "@data/companyInfo";
import { teamMembers } from "@data/teamMembers";
import SEO from "@components/SEO";
import {
  createBusinessStructuredData,
  createBreadcrumbStructuredData,
} from "../utils/structuredData";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// Zod validation schema
const contactSchema = z.object({
  type: z.literal("contact"),
  tourPackage: z.string().min(1, "Tour package selection is required"),
  location: z.string().min(1, "Location selection is required"),
  name: z.string().min(1, "Name is required"),
  email: z.email("Please enter a valid email address"),
  phoneNumber: z.string().min(1, "Phone number is required"),
  adults: z.number().min(1, "At least 1 adult is required"),
  children: z.number().min(0, "Children must be 0 or more"),
  arrivalDate: z
    .string()
    .min(1, "Arrival date is required")
    .refine((date) => {
      const selectedDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    }, "Arrival date cannot be before today"),
  departureDate: z
    .string()
    .min(1, "Departure date is required")
    .refine((date) => {
      const selectedDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    }, "Departure date cannot be before today"),
  additionalNotes: z.string().optional(),
});

type ContactFormDataType = z.infer<typeof contactSchema>;

const TeamContactCard: React.FC<{ member: (typeof teamMembers)[0] }> = ({
  member,
}) => (
  <div className="h-full bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-300">
    <div className="flex items-center mb-4">
      {/* <img
        src={member.profileImage}
        alt={member.name}
        className="w-16 h-16 rounded-full object-cover mr-4"
      /> */}
      <div>
        <h3 className="text-lg font-bold text-primaryText">{member.name}</h3>
        <p className="text-primary font-semibold">{member.position}</p>
      </div>
    </div>

    <div className="space-y-2 mb-4 text-sm">
      {member.email && (
        <div className="flex items-center text-primaryText">
          <svg
            className="w-4 h-4 mr-2 text-primary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <a
            href={`mailto:${member.email}`}
            className="hover:text-primary transition-colors"
          >
            {member.email}
          </a>
        </div>
      )}
      {member.phone && (
        <div className="flex items-center text-primaryText">
          <svg
            className="w-4 h-4 mr-2 text-primary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          <a
            href={`tel:${member.phone}`}
            className="hover:text-primary transition-colors"
          >
            {member.phone}
          </a>
        </div>
      )}
    </div>

    <button
      onClick={() =>
        window.open(
          `https://wa.me/${member.whatsapp.replace("+", "")}?text=Hi ${
            member.name.split(" ")[0]
          }! I'd like to inquire about your tours.`,
          "_blank",
        )
      }
      className="w-full bg-whatsapp cursor-pointer text-white px-4 py-2 rounded-md font-semibold hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
    >
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
      </svg>
      <span>WhatsApp {member.name.split(" ")[0]}</span>
    </button>
  </div>
);

const ContactPage: React.FC = () => {
  const contactUsEndpoint = "/contact-us";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormDataType>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      type: "contact",
      tourPackage: "",
      location: "",
      name: "",
      email: "",
      phoneNumber: "",
      adults: 1,
      children: 0,
      arrivalDate: "",
      departureDate: "",
      additionalNotes: "",
    },
  });

  const contactTeamMembers = teamMembers.filter(
    (member) => member.displayOnContact,
  );

  const onSubmit = async (data: ContactFormDataType) => {
    setIsSubmitting(true);
    setSubmitStatus("idle");

    console.log(
      `Contact form submitted at endpoint: ${
        import.meta.env.VITE_CONTACT_API_URL
      }${contactUsEndpoint}`,
    );

    console.log(data);

    try {
      await axios.post(
        `${import.meta.env.VITE_CONTACT_API_URL}${contactUsEndpoint}`,
        {
          ...data,
          timestamp: new Date().toISOString(),
        },
      );

      setSubmitStatus("success");
      reset();
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Contact Ikhlas Tours | Plan Your New Zealand Tour"
        description="Contact Ikhlas Tours to plan your New Zealand tour from Malaysia, Singapore or Brunei. WhatsApp, email or use the booking form for a customised itinerary."
        path="/contact"
        structuredData={[
          createBusinessStructuredData(companyInfo),
          createBreadcrumbStructuredData([
            { name: "Home", url: "/" },
            { name: "Contact", url: "/contact" },
          ]),
        ]}
      />
      {/* Hero Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-primaryText max-w-3xl mx-auto">
            Get in touch with our team to start planning your New Zealand
            adventure
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Left Column - Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-primaryText mb-6">
                Book Your New Zealand Adventure
              </h2>
              <p className="text-gray-600 mb-6">
                Fill in your travel details below and we'll help you plan the
                perfect New Zealand experience tailored to your preferences.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Tour Package */}
                <div>
                  <label
                    htmlFor="tourPackage"
                    className="block text-sm font-medium text-primaryText mb-2"
                  >
                    Tour Package *
                  </label>
                  <select
                    id="tourPackage"
                    {...register("tourPackage")}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.tourPackage
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="">Select a tour package</option>
                    <option value="Self Drive">Self Drive</option>
                    <option value="Guided Half Board">Guided Half Board</option>
                    <option value="Guided Full Board">Guided Full Board</option>
                    <option value="Motorhome">Motorhome</option>
                  </select>
                  {errors.tourPackage && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.tourPackage.message}
                    </p>
                  )}
                </div>

                {/* Location */}
                <div>
                  <label
                    htmlFor="location"
                    className="block text-sm font-medium text-primaryText mb-2"
                  >
                    Destination *
                  </label>
                  <select
                    id="location"
                    {...register("location")}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.location
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="">Select a destination</option>
                    <option value="North Island">North Island</option>
                    <option value="South Island">South Island</option>
                    <option value="Combo Island">Combo Island</option>
                  </select>
                  {errors.location && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.location.message}
                    </p>
                  )}
                </div>

                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-primaryText mb-2"
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    {...register("name")}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.name
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-primaryText mb-2"
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    {...register("email")}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.email
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Phone Number */}
                <div>
                  <label
                    htmlFor="phoneNumber"
                    className="block text-sm font-medium text-primaryText mb-2"
                  >
                    Phone Number / WhatsApp *
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    {...register("phoneNumber")}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.phoneNumber
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.phoneNumber && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.phoneNumber.message}
                    </p>
                  )}
                </div>

                {/* Adults and Children */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="adults"
                      className="block text-sm font-medium text-primaryText mb-2"
                    >
                      Adults *
                    </label>
                    <input
                      type="number"
                      id="adults"
                      min="0"
                      {...register("adults", { valueAsNumber: true })}
                      className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.adults
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {errors.adults && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.adults.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="children"
                      className="block text-sm font-medium text-primaryText mb-2"
                    >
                      Children *
                    </label>
                    <input
                      type="number"
                      id="children"
                      min="0"
                      {...register("children", { valueAsNumber: true })}
                      className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.children
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {errors.children && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.children.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Arrival and Departure Dates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="arrivalDate"
                      className="block text-sm font-medium text-primaryText mb-2"
                    >
                      Arrival Date *
                    </label>
                    <input
                      type="date"
                      id="arrivalDate"
                      {...register("arrivalDate")}
                      min={new Date().toISOString().split("T")[0]}
                      className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.arrivalDate
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {errors.arrivalDate && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.arrivalDate.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="departureDate"
                      className="block text-sm font-medium text-primaryText mb-2"
                    >
                      Departure Date *
                    </label>
                    <input
                      type="date"
                      id="departureDate"
                      {...register("departureDate")}
                      min={new Date().toISOString().split("T")[0]}
                      className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.departureDate
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {errors.departureDate && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.departureDate.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Additional Notes */}
                <div>
                  <label
                    htmlFor="additionalNotes"
                    className="block text-sm font-medium text-primaryText mb-2"
                  >
                    Additional Notes
                  </label>
                  <textarea
                    id="additionalNotes"
                    rows={6}
                    {...register("additionalNotes")}
                    className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary border-gray-300"
                    placeholder="Tell us about any special requirements, preferences, or questions you have..."
                  />
                </div>

                {submitStatus === "error" && (
                  <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
                    Sorry, there was an error sending your message. Please try
                    again or contact us directly.
                  </div>
                )}

                {submitStatus === "success" && (
                  <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                    Thank you for your message! We'll get back to you soon.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="cursor-pointer w-full bg-primary text-white px-6 py-3 rounded-md font-semibold hover:bg-secondary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>

            {/* Right Column - Team Contact */}
            <div>
              <h2 className="text-2xl font-bold text-primaryText mb-6">
                Contact Our Team
              </h2>
              <p className="text-gray-600 mb-6">
                Have questions about specific tours or services? Contact our
                team members directly for personalised assistance.
              </p>

              <div className="grid grid-cols-1 gap-6">
                {contactTeamMembers.map((member) => (
                  <TeamContactCard key={member.id} member={member} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primaryText mb-4">
              Other Ways to Reach Us
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                TikTok
              </h3>
              <a
                href="https://www.tiktok.com/@ikhlastours"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer inline-block bg-primary text-white px-4 py-2 rounded-md font-semibold hover:bg-secondary hover:text-primary transition-all duration-300"
              >
                Message us on TikTok
              </a>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Facebook
              </h3>
              <a
                href="https://facebook.com/ikhlastours"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer inline-block bg-primary text-white px-4 py-2 rounded-md font-semibold hover:bg-secondary hover:text-primary transition-all duration-300"
              >
                Message us on Facebook
              </a>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Email: {companyInfo.contactEmail}
              </h3>
              <a
                href={`mailto:${companyInfo.contactEmail}`}
                className="cursor-pointer inline-block bg-primary text-white px-4 py-2 rounded-md font-semibold hover:bg-secondary hover:text-primary transition-all duration-300"
              >
                Send us a detailed inquiry
              </a>
            </div>

            {/* <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Visit Us
              </h3>
              <p className="text-gray-600 mb-2">Stop by our office</p>
              <p className="text-primary font-semibold">
                {companyInfo.address}
              </p>
            </div> */}
          </div>
        </div>
      </section>

      {/* Business Hours */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-primaryText mb-6">
            Business Hours
          </h2>
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div>
                <p className="font-semibold text-primaryText mb-2">
                  Monday - Friday
                </p>
                <p className="text-gray-600">9:00 AM - 6:00 PM (NZST)</p>
              </div>
              <div>
                <p className="font-semibold text-primaryText mb-2">
                  Saturday - Sunday
                </p>
                <p className="text-gray-600">10:00 AM - 4:00 PM (NZST)</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Response times may vary outside business hours. We'll get back to
              you as soon as possible!
            </p>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default ContactPage;
